import { useState, useEffect, useCallback, lazy, Suspense } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ErrorBoundary from '../components/ErrorBoundary'
import Preloader from '../components/Preloader'
const CustomCursor = lazy(() => import('../components/CustomCursor'))
import QuoteBreak from '../components/QuoteBreak'
import VolumeKnob from '../components/VolumeKnob'
const Discovery = lazy(() => import('../components/Discovery'))
import Testimonials from '../components/Testimonials'
import StickyCart from '../components/StickyCart'
import Footer from '../components/Footer'
import CartDrawer from '../components/CartDrawer'
import CookieConsent from '../components/CookieConsent'
import ScrollProgress from '../components/ScrollProgress'
import LangToggle from '../components/LangToggle'

const Hero = lazy(() => import('../components/Hero'))
const ParticleField = lazy(() => import('../components/ParticleField'))

function HeroFallback() {
  const { t } = useTranslation()
  return (
    <section className="hero">
      <div className="hero-content">
        <a href="/login" className="hero-cta">{t('hero.cta')}</a>
      </div>
    </section>
  )
}

export default function MarketingSite() {
  const { t } = useTranslation()
  const [preloading, setPreloading] = useState(true)
  const [menuOpen, setMenuOpen] = useState(false)
  const [heroDeferDone, setHeroDeferDone] = useState(false)
  const isTouch = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0)

  // Remove static HTML nav once React nav renders
  useEffect(() => {
    document.getElementById('static-nav')?.remove()
  }, [])

  const handlePreloadComplete = useCallback(() => setPreloading(false), [])

  // Defer Hero (Three.js) until after first paint (desktop and mobile both get Hero after idle)
  useEffect(() => {
    if (preloading) return
    let cancelled = false
    const afterPaint = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelled) setHeroDeferDone(true)
        })
      })
    }
    const useIdle = typeof requestIdleCallback !== 'undefined'
    const id = useIdle ? requestIdleCallback(afterPaint, { timeout: 2000 }) : setTimeout(afterPaint, 0)
    return () => {
      cancelled = true
      useIdle ? cancelIdleCallback(id) : clearTimeout(id)
    }
  }, [preloading])

  // Deferred: gsap + Lenis + ScrollTrigger only after preload (keeps initial bundle smaller for FCP)
  useEffect(() => {
    if (preloading) return

    let destroyed = false
    let cleanup = null
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')

    Promise.all([
      import('lenis'),
      import('gsap'),
      import('gsap/ScrollTrigger'),
    ]).then(([lenisMod, gsapMod, stMod]) => {
      if (destroyed) return
      const gsap = gsapMod.default
      const ScrollTrigger = stMod.default
      gsap.registerPlugin(ScrollTrigger)

      if (mq.matches) gsap.globalTimeline.timeScale(20)
      const onChange = (e) => { gsap.globalTimeline.timeScale(e.matches ? 20 : 1) }
      mq.addEventListener('change', onChange)

      const Lenis = lenisMod.default
      const lenis = new Lenis({
        duration: mq.matches ? 0 : 1.4,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: !mq.matches,
      })
      lenis.on('scroll', ScrollTrigger.update)

      const rafCallback = (time) => { lenis.raf(time * 1000) }
      gsap.ticker.add(rafCallback)
      gsap.ticker.lagSmoothing(0)

      return () => {
        mq.removeEventListener('change', onChange)
        gsap.ticker.remove(rafCallback)
        lenis.destroy()
        ScrollTrigger.getAll().forEach((st) => st.kill())
      }
    }).then((fn) => {
      if (fn) cleanup = fn
      if (destroyed && cleanup) cleanup()
    })

    return () => {
      destroyed = true
      if (cleanup) cleanup()
    }
  }, [preloading])

  return (
    <>
      <Preloader onComplete={handlePreloadComplete} />

      {!preloading && (
        <ErrorBoundary>
          <a href="#main" className="skip-link">Skip to main content</a>
          {!isTouch && <Suspense fallback={null}><CustomCursor /></Suspense>}
          <Suspense fallback={null}>
            <ParticleField />
          </Suspense>
          <div className="noise-overlay" aria-hidden="true" />
          <ScrollProgress />
          <StickyCart />
          <CartDrawer />

          <nav className="nav" role="navigation">
            <div className="nav-logo">
              <span className="omg">Opus Magnum Ghost</span>
            </div>
            <ul className={`nav-links ${menuOpen ? 'nav-open' : ''}`}>
              <li><a href="#system" onClick={() => setMenuOpen(false)}>{t('nav.system')}</a></li>
              <li><Link to="/blog" onClick={() => setMenuOpen(false)}>{t('nav.blog')}</Link></li>
              <li><Link to="/login" onClick={() => setMenuOpen(false)}>{t('nav.acquire')}</Link></li>
              <li><Link to="/dashboard" onClick={() => setMenuOpen(false)}>{t('nav.dashboard')}</Link></li>
              <li><LangToggle /></li>
            </ul>
            <button
              className={`nav-burger ${menuOpen ? 'nav-burger-open' : ''}`}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
            >
              <span /><span /><span />
            </button>
          </nav>

          <div id="main">
            {heroDeferDone ? (
              <Suspense fallback={<HeroFallback />}>
                <Hero />
              </Suspense>
            ) : (
              <HeroFallback />
            )}
            <QuoteBreak />
            <VolumeKnob />
            <Suspense fallback={null}><Discovery /></Suspense>
            <Testimonials />
          </div>
          <Footer />
          <CookieConsent />
        </ErrorBoundary>
      )}
    </>
  )
}
